import {validateQualificationRun, qualificationChecklistKeys} from '../scripts/qualification-evidence.mjs';

const checklistLabels = {
  actualPrintedArtwork: 'Actual printed artwork and difficult print variants',
  completeTargetSet: 'Complete target set',
  completeVideoSet: 'Complete video set',
  presentationModes: 'Presentation modes: fit, fill, center, legacy',
  priorityChanges: 'Priority changes while targets remain tracked',
  concurrentDifferentVideos: 'Concurrent different videos',
  sameVideoPlaybackPosition: 'Shared playback position across anchors for one video',
  looping: 'Video looping',
  lossAndResume: 'Tracking loss and resume',
  rangeSeekAndResume: 'Range seek and resume',
  networkInterruption: 'Network interruption and recovery',
  backgroundForeground: 'Background and foreground recovery',
  repeatedSessionCleanup: 'Repeated session start, stop, and cleanup',
  embeddedEntryAndHandoff: 'Embedded browser entry and external-browser handoff',
};

const modes = ['fit', 'fill', 'center', 'legacy'];
const choices = (values, labels = values) => values.map((value, index) =>
  `<option value="${value}">${labels[index]}</option>`).join('');
const checklistMarkup = qualificationChecklistKeys.map(key => `
  <div class="qar-check" data-check="${key}">
    <label>${checklistLabels[key]}
      <select data-check-state="${key}">
        ${choices(['not-run', 'observed', 'passed', 'gap'], ['Not run', 'Observed', 'Passed', 'Gap'])}
      </select>
    </label>
    <label>Note <input data-check-note="${key}" type="text" maxlength="500" /></label>
  </div>`).join('');

const field = (name, label, type = 'text', extra = '') =>
  `<label>${label}<input data-field="${name}" type="${type}" ${extra} /></label>`;
const select = (name, label, values, labels = values) =>
  `<label>${label}<select data-field="${name}">${choices(values, labels)}</select></label>`;
const checkbox = (name, label) =>
  `<label class="qar-inline"><input data-field="${name}" type="checkbox" /> ${label}</label>`;

function textValue(form, name) {
  const value = form.querySelector(`[data-field="${name}"]`)?.value?.trim() ?? '';
  return value.length ? value : null;
}

function numberValue(form, name) {
  const raw = form.querySelector(`[data-field="${name}"]`)?.value?.trim() ?? '';
  if (!raw) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : Number.NaN;
}

function integerValue(form, name) {
  const value = numberValue(form, name);
  return value === null || !Number.isInteger(value) ? value : value;
}

function nullableText(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function finiteOrNull(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function dimensions(value) {
  if (!value || !Number.isFinite(value.widthPx) || !Number.isFinite(value.heightPx)) return null;
  return {widthPx: value.widthPx, heightPx: value.heightPx};
}

function projectVideoPlayer(video) {
  return {
    videoId: nullableText(video?.videoId),
    widthPx: finiteOrNull(video?.widthPx),
    heightPx: finiteOrNull(video?.heightPx),
    durationSeconds: finiteOrNull(video?.durationSeconds),
    positionSeconds: finiteOrNull(video?.positionSeconds),
    paused: typeof video?.paused === 'boolean' ? video.paused : null,
    muted: typeof video?.muted === 'boolean' ? video.muted : null,
    waitingEvents: finiteOrNull(video?.waitingEvents),
    playingEvents: finiteOrNull(video?.playingEvents),
    errorEvents: finiteOrNull(video?.errorEvents),
    decodedFrames: finiteOrNull(video?.decodedFrames),
    droppedFrames: finiteOrNull(video?.droppedFrames),
    urlRenewals: finiteOrNull(video?.urlRenewals ?? video?.renewalCount),
    lastUrlRenewalAtMs: finiteOrNull(video?.lastUrlRenewalAtMs),
  };
}

export function qualificationDiagnosticsSessionMarker(windowRef) {
  const api = windowRef?.__browserArDiagnostics;
  if (!api) return {viewerState: 'unavailable', sessionGeneration: null};
  try {
    return {
      viewerState: typeof api.state === 'string' ? api.state : 'unavailable',
      sessionGeneration: Number.isInteger(api.sessionGeneration) ? api.sessionGeneration : null,
    };
  } catch {
    return {viewerState: 'unavailable', sessionGeneration: null};
  }
}

export function cameraStartupDiagnosticText(windowRef) {
  let source;
  try { source = windowRef?.__browserArDiagnostics?.cameraStartup; }
  catch { source = null; }
  if (!source || typeof source !== 'object') return 'No camera startup recorded.';
  return JSON.stringify(projectCameraStartupDiagnostic(source), null, 2);
}

export async function copyCameraStartupDiagnostic(windowRef, output) {
  const text = cameraStartupDiagnosticText(windowRef);
  output.value = text;
  const clipboard = windowRef?.navigator?.clipboard;
  try {
    if (typeof clipboard?.writeText !== 'function') throw new Error('Clipboard API unavailable.');
    await clipboard.writeText(text);
    return {copied: true, text};
  } catch {
    output.focus?.();
    output.select?.();
    return {copied: false, text};
  }
}

export function projectCameraStartupDiagnostic(source) {
  const finite = value => typeof value === 'number' && Number.isFinite(value) ? value : null;
  const positive = value => finite(value) !== null && value > 0 ? Math.round(value) : null;
  const timestamp = value => typeof value === 'string' && Number.isFinite(Date.parse(value))
    ? new Date(value).toISOString()
    : null;
  const track = value => ({
    kind: value?.kind === 'video' ? 'video' : 'other',
    enabled: typeof value?.enabled === 'boolean' ? value.enabled : null,
    muted: typeof value?.muted === 'boolean' ? value.muted : null,
    readyState: ['live', 'ended'].includes(value?.readyState) ? value.readyState : null,
  });
  const video = value => value && typeof value === 'object' ? {
    connected: typeof value.connected === 'boolean' ? value.connected : null,
    paused: typeof value.paused === 'boolean' ? value.paused : null,
    readyState: Number.isInteger(value.readyState) && value.readyState >= 0 && value.readyState <= 4 ? value.readyState : null,
    currentTimeSeconds: finite(value.currentTimeSeconds),
    widthPx: positive(value.widthPx),
    heightPx: positive(value.heightPx),
    rect: value.rect && typeof value.rect === 'object' ? {
      left: finite(value.rect.left), top: finite(value.rect.top),
      width: finite(value.rect.width), height: finite(value.rect.height),
    } : null,
    inViewport: typeof value.inViewport === 'boolean' ? value.inViewport : null,
    style: value.style && typeof value.style === 'object' ? {
      display: ['none', 'block', 'inline', 'inline-block', 'contents', 'other'].includes(value.style.display) ? value.style.display : 'unknown',
      visibility: ['visible', 'hidden', 'collapse', 'other'].includes(value.style.visibility) ? value.style.visibility : 'unknown',
      opacity: finite(value.style.opacity),
    } : null,
    documentHidden: typeof value.documentHidden === 'boolean' ? value.documentHidden : null,
    visibilityState: ['visible', 'hidden', 'prerender', 'unknown'].includes(value.visibilityState) ? value.visibilityState : 'unknown',
  } : null;
  const safeStages = new Set([
    'start-requested', 'camera-requesting', 'camera-hasStream', 'camera-hasVideo', 'camera-failed',
    'camera-video-size', 'first-xr-update', 'scene-ready', 'runtime-ready', 'startup-timeout',
    'session-stopped', 'startup-failed',
  ]);
  const events = Array.isArray(source.events) ? source.events.slice(-24).map(event => ({
    stage: safeStages.has(event?.stage) ? event.stage : 'other',
    at: timestamp(event?.at),
    elapsedMs: Math.max(0, finite(event?.elapsedMs) ?? 0),
    ...(event?.details && typeof event.details === 'object' ? {
      details: {
        ...( ['DENY_CAMERA', 'NO_CAMERA', 'UNSPECIFIED'].includes(event.details.reason) ? {reason: event.details.reason} : {}),
        ...(positive(event.details.widthPx) ? {widthPx: positive(event.details.widthPx)} : {}),
        ...(positive(event.details.heightPx) ? {heightPx: positive(event.details.heightPx)} : {}),
      },
    } : {}),
  })) : [];
  const safeFailureStages = new Set([
    'camera stream was not delivered',
    'XR8 camera video element was not available',
    'camera video did not deliver a decoded frame',
    'AR scene did not start after camera frames arrived',
    'XR8 startup did not finish after the scene started',
    'camera and scene reported ready before timeout',
  ]);
  const state = ['starting', 'ready', 'timed-out', 'failed', 'stopped'].includes(source.state) ? source.state : 'unavailable';
  const lastSample = source.camera?.lastSample;
  return {
    schemaVersion: 1,
    state,
    startedAt: timestamp(source.startedAt),
    finishedAt: timestamp(source.finishedAt),
    failureStage: safeFailureStages.has(source.failureStage) ? source.failureStage : null,
    events,
    camera: {
      tracksOnStream: Array.isArray(source.camera?.tracksOnStream) ? source.camera.tracksOnStream.slice(0, 4).map(track) : [],
      lastSample: lastSample && typeof lastSample === 'object' ? {
        at: timestamp(lastSample.at),
        tracks: Array.isArray(lastSample.tracks) ? lastSample.tracks.slice(0, 4).map(track) : [],
        video: video(lastSample.video),
      } : null,
    },
  };
}

export function projectQualificationDiagnostics(windowRef, {
  trialStartAt = null,
  trialEndAt = null,
  startMarker = null,
  snapshotAt = new Date().toISOString(),
} = {}) {
  const api = windowRef?.__browserArDiagnostics ?? null;
  let viewerStateAtSnapshot = 'unavailable';
  let sessionGeneration = null;
  let sessionStartedAt = null;
  let sessionRunningAt = null;
  let metrics = null;
  let platform = null;
  let targetSetReady = null;
  try {
    if (api) {
      viewerStateAtSnapshot = typeof api.state === 'string' ? api.state : 'unavailable';
      sessionGeneration = Number.isInteger(api.sessionGeneration) ? api.sessionGeneration : null;
      sessionStartedAt = nullableText(api.sessionStartedAt);
      sessionRunningAt = nullableText(api.sessionRunningAt);
      platform = nullableText(api.platform);
      targetSetReady = typeof api.targetSetReady === 'boolean' ? api.targetSetReady : null;
      // Read only the documented lazy diagnostics property. Never serialize the global object.
      metrics = api.metrics ?? null;
    }
  } catch {
    metrics = null;
  }

  const hasTrialWindow = isIsoTimestamp(trialStartAt) && isIsoTimestamp(trialEndAt);
  const sessionStartsDelta = Number.isInteger(startMarker?.sessionGeneration) && Number.isInteger(sessionGeneration)
    ? sessionGeneration - startMarker.sessionGeneration : null;
  const sessionStartsDuringTrial = sessionStartsDelta !== null && sessionStartsDelta >= 0 ? sessionStartsDelta : null;
  const sessionStartedMs = Date.parse(sessionStartedAt ?? '');
  const sessionRunningMs = Date.parse(sessionRunningAt ?? '');
  const startMs = Date.parse(trialStartAt ?? '');
  const endMs = Date.parse(trialEndAt ?? '');
  const singleFreshSessionRanDuringTrial = hasTrialWindow && sessionStartsDuringTrial === 1 &&
    !['running', 'paused'].includes(startMarker?.viewerState) &&
    Number.isFinite(sessionStartedMs) && sessionStartedMs >= startMs && sessionStartedMs <= endMs &&
    Number.isFinite(sessionRunningMs) && sessionRunningMs >= sessionStartedMs && sessionRunningMs <= endMs;
  const state = singleFreshSessionRanDuringTrial ? 'captured' : 'unavailable';
  const capturedMetrics = state === 'captured' ? metrics : null;
  const camera = capturedMetrics?.camera ?? {};
  const video = capturedMetrics?.video ?? null;
  const pose = capturedMetrics?.relativePose ?? null;
  return {
    state,
    snapshotAt,
    viewerStateAtSnapshot,
    sessionGeneration,
    sessionStartsDuringTrial,
    sessionStartedAt,
    sessionRunningAt,
    viewerStateAtTrialStart: hasTrialWindow ? startMarker?.viewerState ?? 'unavailable' : null,
    sessionGenerationAtTrialStart: hasTrialWindow ? startMarker?.sessionGeneration ?? null : null,
    platform: state === 'captured' ? platform : null,
    targetSetReady: state === 'captured' ? targetSetReady : null,
    targetAcquisitions: finiteOrNull(capturedMetrics?.targetAcquisitions),
    targetLosses: finiteOrNull(capturedMetrics?.targetLosses),
    processedFreshFrameCount: finiteOrNull(capturedMetrics?.processedFreshFrameCount),
    processedFreshFramesPerSecond: finiteOrNull(capturedMetrics?.processedFreshFramesPerSecond),
    xrUpdateCallbacks: finiteOrNull(capturedMetrics?.xrUpdateCallbacks),
    xrUpdateCallbacksPerSecond: finiteOrNull(capturedMetrics?.xrUpdateCallbackFps),
    targetFoundToFirstFreshXrFrameMs: finiteOrNull(capturedMetrics?.targetFoundToFirstFreshXrFrameMs),
    lastFrameRepeatStatus: ['fresh', 'repeat', 'unavailable'].includes(capturedMetrics?.lastFrameRepeatStatus)
      ? capturedMetrics.lastFrameRepeatStatus : 'unavailable',
    cameraResolutionSource: nullableText(camera.resolutionSource),
    cameraTrackStatus: nullableText(camera.trackStatus),
    videoWaitingEvents: finiteOrNull(video?.waitingEvents),
    videoPlayingEvents: finiteOrNull(video?.playingEvents),
    videoErrorEvents: finiteOrNull(video?.errorEvents),
    decodedVideoFrames: finiteOrNull(video?.decodedFrames),
    droppedVideoFrames: finiteOrNull(video?.droppedFrames),
    cameraResolutionPx: dimensions(camera.resolution),
    cameraTrackResolutionPx: dimensions(camera.trackResolution),
    videoResolutionPx: dimensions(video),
    // Copy only documented per-player fields; never copy media URLs or DOM objects.
    videoPlayers: Array.isArray(capturedMetrics?.videos) ? capturedMetrics.videos.map(projectVideoPlayer) : [],
    concurrentPlayingCountHighWater: finiteOrNull(capturedMetrics?.concurrentPlayingCountHighWater),
    concurrentPlaybackDurationMs: finiteOrNull(capturedMetrics?.concurrentPlaybackDurationMs),
    concurrentPlaybackDurationsByCount: Array.isArray(capturedMetrics?.concurrentPlaybackDurationsByCount)
      ? capturedMetrics.concurrentPlaybackDurationsByCount.map(entry => ({
        minimumVideoCount: finiteOrNull(entry?.minimumVideoCount),
        durationMs: finiteOrNull(entry?.durationMs),
      }))
      : [],
    relativePose: pose ? {
      marked: Boolean(pose.marked),
      windowDurationMs: finiteOrNull(pose.windowDurationMs),
      samples: finiteOrNull(pose.samples),
      translationDeltaMeanXrUnits: finiteOrNull(pose.translationDeltaMeanXrUnits),
      translationDeltaMaxXrUnits: finiteOrNull(pose.translationDeltaMaxXrUnits),
      rotationDeltaMeanDegrees: finiteOrNull(pose.rotationDeltaMeanDegrees),
      rotationDeltaMaxDegrees: finiteOrNull(pose.rotationDeltaMaxDegrees),
      interpretation: nullableText(pose.interpretation) ?? 'Relative XR pose change only; no physical registration ground truth.',
    } : null,
  };
}

function isIsoTimestamp(value) {
  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}

function addStyle(documentRef) {
  const style = documentRef.createElement('style');
  style.textContent = `
    #browser-ar-qualification { position:fixed; z-index:2147483000; right:12px; bottom:12px; font:14px/1.4 system-ui,sans-serif; color:#172033; }
    #browser-ar-qualification * { box-sizing:border-box; }
    #browser-ar-qualification button { border:1px solid #657087; border-radius:6px; background:#fff; color:#172033; padding:8px 10px; font:inherit; cursor:pointer; }
    #browser-ar-qualification .qar-open { box-shadow:0 2px 16px #0003; font-weight:700; }
    #browser-ar-qualification .qar-panel { display:none; width:min(620px,calc(100vw - 24px)); max-height:min(86vh,900px); overflow:auto; background:#fff; border:1px solid #657087; border-radius:10px; box-shadow:0 8px 32px #0005; padding:14px; }
    #browser-ar-qualification[data-open="true"] .qar-panel { display:block; }
    #browser-ar-qualification[data-open="true"] .qar-open { display:none; }
    #browser-ar-qualification header { position:sticky; top:-14px; z-index:1; display:flex; justify-content:space-between; align-items:center; background:#fff; padding:4px 0 10px; }
    #browser-ar-qualification h2 { font-size:18px; margin:0; }
    #browser-ar-qualification h3 { font-size:15px; margin:16px 0 8px; }
    #browser-ar-qualification p { margin:6px 0; }
    #browser-ar-qualification .qar-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:9px; }
    #browser-ar-qualification label { display:flex; flex-direction:column; gap:3px; font-size:12px; font-weight:650; }
    #browser-ar-qualification input, #browser-ar-qualification select, #browser-ar-qualification textarea { min-width:0; width:100%; padding:7px; border:1px solid #aab2bf; border-radius:5px; background:#fff; color:#172033; font:14px system-ui,sans-serif; }
    #browser-ar-qualification .qar-inline { display:inline-flex; flex-direction:row; align-items:center; margin:8px 12px 0 0; }
    #browser-ar-qualification .qar-inline input { width:auto; }
    #browser-ar-qualification .qar-check { display:grid; grid-template-columns:1.3fr 1fr; gap:8px; margin:7px 0; }
    #browser-ar-qualification .qar-actions { display:flex; flex-wrap:wrap; gap:7px; margin:8px 0; }
    #browser-ar-qualification .qar-note { font-size:12px; color:#46536a; }
    #browser-ar-qualification .qar-warning { color:#842029; font-weight:700; }
    #browser-ar-qualification [role="status"] { white-space:pre-wrap; overflow-wrap:anywhere; }
    @media(max-width:480px) { #browser-ar-qualification .qar-grid, #browser-ar-qualification .qar-check { grid-template-columns:1fr; } }
  `;
  documentRef.head.append(style);
}

/** Mount the opt-in, memory-only field-capture UI. No evidence is stored or uploaded. */
export function mountQualificationCapture({windowRef = globalThis.window, documentRef = globalThis.document} = {}) {
  if (!windowRef || !documentRef || new URLSearchParams(windowRef.location.search).get('arDiagnostics') !== '1') return null;
  if (documentRef.getElementById('browser-ar-qualification')) return null;

  addStyle(documentRef);
  const root = documentRef.createElement('div');
  root.id = 'browser-ar-qualification';
  root.dataset.open = 'false';
  const currentIdentifier = windowRef.location.pathname.split('/').filter(Boolean)[0] ?? '';
  root.innerHTML = `
    <button type="button" class="qar-open" aria-expanded="false">Qualification capture</button>
    <section class="qar-panel" role="dialog" aria-label="Browser AR qualification capture">
      <header><h2>Device qualification evidence</h2><button type="button" data-action="close" aria-label="Close capture panel">Close</button></header>
      <p class="qar-note">Memory-only: export downloads a JSON record to this device. Nothing is uploaded; camera frames, signed URLs, and session credentials are excluded.</p>
      <section aria-label="Camera startup diagnostics">
        <h3>Camera startup</h3>
        <p class="qar-note">For startup troubleshooting only. The copied log contains timings and camera readiness state, without camera images or identifying track details.</p>
        <textarea data-role="camera-startup-log" rows="7" readonly aria-label="Camera startup diagnostic log"></textarea>
        <div class="qar-actions"><button type="button" data-action="copy-camera-startup">Copy camera startup log</button></div>
        <p data-role="camera-startup-copy-status" role="status" aria-live="polite"></p>
      </section>
      <p data-role="build-status" role="status"></p>
      <form novalidate>
        <h3>Device and build</h3>
        <div class="qar-grid">
          ${select('deviceFamily', 'Device family', ['galaxy-a14', 'pixel-7', 'other'], ['Galaxy A14', 'Pixel 7', 'Other / exploratory'])}
          ${field('deviceModel', 'Model as shown in device settings')}
          ${field('deviceVariant', 'Variant / model code')}
          ${field('osVersion', 'OS version')}
          ${field('buildNumber', 'OS build number')}
          ${select('browserFamily', 'Browser family', ['chrome', 'samsung-internet', 'embedded-other', 'other'], ['Chrome', 'Samsung Internet', 'Embedded / other host', 'Other'])}
          ${field('browserName', 'Installed browser name')}
          ${field('browserVersion', 'Full browser version')}
          ${field('embeddedHost', 'Embedded host (if applicable)')}
          ${field('runtimeVersion', 'Browser AR runtime version', 'text', 'value="0.1.0"')}
          ${field('engineVersion', '8th Wall engine version', 'text', 'value="0.1.0"')}
          ${field('seriesId', 'Repeat-trial series UUID', 'text', `value="${windowRef.crypto?.randomUUID?.() ?? ''}"`)}
        </div>

        <h3>Printed fixture and complete content</h3>
        <div class="qar-grid">
          ${field('identifier', 'Published experience code', 'text', `value="${/^[A-Za-z0-9]{10}$/.test(currentIdentifier) ? currentIdentifier : ''}"`)}
          ${field('contentRevision', 'Published content revision')}
          ${field('printLabel', 'Printed artwork / revision label')}
          ${field('expectedTargets', 'Expected targets', 'number', 'min="1" step="1"')}
          ${field('loadedTargets', 'Loaded targets', 'number', 'min="0" step="1"')}
          ${field('expectedVideos', 'Expected videos', 'number', 'min="1" step="1"')}
          ${field('loadedVideos', 'Loaded videos', 'number', 'min="0" step="1"')}
          ${field('concurrentVideosExpected', 'Distinct videos expected to play at once', 'number', 'min="1" step="1" value="1"')}
          <label>Presentation modes
            <select data-field="presentationModes" multiple size="4">${choices(modes)}</select>
          </label>
        </div>
        <p class="qar-note">Keep the concurrent count at 1 for a single-video run. For a distinct-video concurrency trial, enter the number you will actually observe playing at the same time; this is separate from the full manifest video count.</p>

        <h3>Trial conditions</h3>
        <div class="qar-grid">
          ${select('phase', 'Comparison phase', ['baseline', 'tuned', 'follow-up'], ['Baseline', 'Tuned', 'Follow-up'])}
          ${select('trialState', 'Trial completion', ['completed', 'incomplete'], ['Completed', 'Incomplete / interrupted'])}
          ${select('measurementPurpose', 'Measurement purpose', ['performance', 'visual-observation'], ['Performance (no external camera)', 'Visual observation (external camera allowed)'])}
          ${select('entryMethod', 'Entry method', ['direct', 'embedded', 'external-handoff', 'other'], ['Direct browser link', 'Embedded browser', 'External-browser handoff', 'Other'])}
          ${field('network', 'Network and signal conditions')}
          ${field('lighting', 'Lighting and glare conditions')}
          ${field('cameraSetting', 'Camera setting / requested capture size')}
          ${field('printDistanceCm', 'Approx. print distance (cm)', 'number', 'min="0" step="any"')}
          ${field('viewingAngleDegrees', 'Approx. viewing angle (degrees)', 'number', 'min="0" step="any"')}
        </div>
        ${checkbox('screencastingEnabled', 'Remote screencasting enabled')}
        ${checkbox('renderCameraTexture', 'renderCameraTexture enabled')}
        ${checkbox('externalCameraUsed', 'External camera used for this observation')}
        <p class="qar-note">Performance trials must have screencasting off and no external-camera observation. Manual alignment timing and print-edge registration belong in a separate visual-observation run. XR-update and fresh-frame rates span their first-to-last callback/frame and include later gaps (including background), but exclude camera permission and startup before the first callback. The stationary-pose window does not reset these counters.</p>

        <h3>Manual timeline</h3>
        <div class="qar-actions">
          <button type="button" data-action="begin">Begin run</button>
          <button type="button" data-action="end">End run</button>
          ${[['camera-ready', 'Camera ready'], ['first-aligned-frame', 'Mark first visibly aligned overlay'],
            ['target-lost', 'Target lost'], ['target-reacquired', 'Target reacquired'], ['video-started', 'Video started'],
            ['video-rebuffered', 'Video rebuffered'], ['stationary-window-marked', 'Mark stationary window']]
            .map(([event, label]) => `<button type="button" data-mark="${event}">${label}</button>`).join('')}
          <button type="button" data-action="reset-pose">Reset pose window</button>
        </div>
        <p class="qar-note">The first-aligned-overlay mark is a human visual observation timed from Begin run; it excludes page load and API session admission. It is separate from the first-acquisition-only diagnostic interval from target-found to the next fresh XR8 frame. Mark stationary only after holding the printed target steady; pose deltas are relative XR units/degrees, not physical registration error.</p>
        <p data-role="trial-status" role="status">No run active.</p>

        <h3>Manual observations</h3>
        <div class="qar-grid">
          <label>Begin-run to first visibly aligned overlay time (ms; manual event mark)<input data-field="beginToFirstAlignedFrameMs" type="number" min="0" step="any" readonly /></label>
          <label>Timing method<textarea data-field="alignedOverlayTimingMethod" rows="2" readonly></textarea></label>
          ${field('registrationOffsetHorizontalMm', 'Print-edge horizontal offset (mm; signed)', 'number', 'step="any"')}
          ${field('registrationOffsetVerticalMm', 'Print-edge vertical offset (mm; signed)', 'number', 'step="any"')}
          ${field('registrationOffsetReference', 'Offset reference edge / landmark')}
          ${field('registrationOffsetMethod', 'Offset method (e.g. coplanar ruler)')}
          ${field('videoStartupMs', 'Manual video startup duration from Begin run (ms)', 'number', 'min="0" step="any"')}
          ${field('concurrentVideosObserved', 'Distinct videos observed playing at once (manual)', 'number', 'min="0" step="1"')}
          ${field('falsePositiveCount', 'False-positive count', 'number', 'min="0" step="1"')}
          ${field('rebufferCount', 'Manual rebuffer count', 'number', 'min="0" step="1"')}
          ${select('qualityRating', 'Subjective quality (not a pass threshold)', ['unrated', 'poor', 'fair', 'good', 'excellent'])}
        </div>
        <div class="qar-grid">
          <label>Tracking and reacquisition notes<textarea data-field="tracking" rows="2"></textarea></label>
          <label>Lag, glare, and occlusion<textarea data-field="lag" rows="2"></textarea></label>
          <label>Glare / occlusion details<textarea data-field="glareAndOcclusion" rows="2"></textarea></label>
          <label>Thermal observations<textarea data-field="thermal" rows="2"></textarea></label>
          <label>Memory observations<textarea data-field="memory" rows="2"></textarea></label>
          <label>Failures / known gaps<textarea data-field="failures" rows="2"></textarea></label>
          <label>Tuning changes<textarea data-field="tuningChanges" rows="2"></textarea></label>
          <label>Other observations<textarea data-field="observations" rows="2"></textarea></label>
        </div>

        <h3>Behavior coverage</h3>
        ${checklistMarkup}
        <div class="qar-actions"><button type="button" data-action="export">Validate and download JSON</button></div>
        <p data-role="validation" role="status" aria-live="polite"></p>
      </form>
    </section>`;
  documentRef.body.append(root);

  const form = root.querySelector('form');
  const panelToggle = root.querySelector('.qar-open');
  const cameraStartupOutput = root.querySelector('[data-role="camera-startup-log"]');
  const cameraStartupCopyStatus = root.querySelector('[data-role="camera-startup-copy-status"]');
  const buildStatus = root.querySelector('[data-role="build-status"]');
  const trialStatus = root.querySelector('[data-role="trial-status"]');
  const validation = root.querySelector('[data-role="validation"]');
  const fieldNode = name => form.querySelector(`[data-field="${name}"]`);
  let startedAt = null;
  let endedAt = null;
  let startedMono = null;
  let timeline = [];
  let runId = null;
  let exported = false;
  let diagnosticsStartMarker = null;
  let frozenDiagnostics = null;
  const viewerBuildId = documentRef.querySelector('meta[name="build-id"]')?.content ?? '';

  function syncPurposeControls() {
    const performanceRun = fieldNode('measurementPurpose').value === 'performance';
    for (const name of ['beginToFirstAlignedFrameMs', 'alignedOverlayTimingMethod', 'registrationOffsetHorizontalMm',
      'registrationOffsetVerticalMm', 'registrationOffsetReference', 'registrationOffsetMethod']) {
      fieldNode(name).disabled = performanceRun;
      if (performanceRun) fieldNode(name).value = '';
    }
    const alignmentMark = root.querySelector('[data-mark="first-aligned-frame"]');
    alignmentMark.disabled = performanceRun;
    if (performanceRun) timeline = timeline.filter(event => event.event !== 'first-aligned-frame');
  }
  fieldNode('measurementPurpose').addEventListener('change', syncPurposeControls);
  syncPurposeControls();

  const setOpen = open => {
    root.dataset.open = String(open);
    panelToggle.setAttribute('aria-expanded', String(open));
  };
  const refreshCameraStartup = () => {
    const text = cameraStartupDiagnosticText(windowRef);
    cameraStartupOutput.value = text;
    return text;
  };
  panelToggle.addEventListener('click', () => {
    setOpen(true);
    refreshCameraStartup();
  });
  root.querySelector('[data-action="copy-camera-startup"]').addEventListener('click', async () => {
    const result = await copyCameraStartupDiagnostic(windowRef, cameraStartupOutput);
    cameraStartupCopyStatus.textContent = result.copied
      ? 'Camera startup log copied.'
      : 'Clipboard unavailable. The log is selected so you can copy it manually.';
  });
  root.querySelector('[data-action="close"]').addEventListener('click', () => setOpen(false));

  buildStatus.textContent = viewerBuildId
    ? `Viewer build: ${viewerBuildId}`
    : 'Viewer build unavailable. Use a deployed Viewer build before qualification.';
  buildStatus.classList.toggle('qar-warning', !viewerBuildId);

  function mark(event) {
    if (!startedAt || endedAt !== null) {
      trialStatus.textContent = 'Begin an active run before adding timeline marks.';
      return;
    }
    const now = new Date();
    const elapsedMs = startedMono === null ? null : Math.max(0, performance.now() - startedMono);
    const note = event === 'first-aligned-frame'
      ? 'Human-identified first visibly aligned overlay; manually observed, not the XR8 fresh-frame metric.'
      : null;
    timeline.push({event, elapsedMs, at: now.toISOString(), ...(note ? {note} : {})});
    if (event === 'first-aligned-frame' && elapsedMs !== null) {
      fieldNode('beginToFirstAlignedFrameMs').value = elapsedMs.toFixed(1);
      fieldNode('alignedOverlayTimingMethod').value = 'Manual operator mark from Begin run until the overlay first appears visibly aligned with the print.';
    }
    if (event === 'video-started' && elapsedMs !== null && !fieldNode('videoStartupMs').value) {
      fieldNode('videoStartupMs').value = elapsedMs.toFixed(1);
    }
    trialStatus.textContent = `${timeline.length} manual event mark(s); latest: ${event}${elapsedMs === null ? '' : ` at ${elapsedMs.toFixed(0)} ms`}.`;
  }

  root.querySelector('[data-action="begin"]').addEventListener('click', () => {
    if (startedAt && endedAt === null) {
      trialStatus.textContent = 'End the current run before starting another one.';
      return;
    }
    if (startedAt && endedAt !== null && !exported && !windowRef.confirm('Discard this in-memory run and begin a new one?')) return;
    startedAt = new Date().toISOString();
    endedAt = null;
    startedMono = performance.now();
    timeline = [];
    diagnosticsStartMarker = qualificationDiagnosticsSessionMarker(windowRef);
    frozenDiagnostics = null;
    runId = windowRef.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
    exported = false;
    mark('trial-start');
    trialStatus.textContent = `Run ${runId} started. Launch one fresh AR session now; an already-running or restarted session will leave performance counters unavailable.`;
    validation.textContent = '';
  });
  root.querySelector('[data-action="end"]').addEventListener('click', () => {
    if (startedAt === null || endedAt !== null) return;
    mark('trial-end');
    endedAt = new Date().toISOString();
    frozenDiagnostics = projectQualificationDiagnostics(windowRef, {
      trialStartAt: startedAt,
      trialEndAt: endedAt,
      startMarker: diagnosticsStartMarker,
    });
    const diagnosticMessage = frozenDiagnostics.state === 'captured'
      ? `Frozen diagnostics for the single fresh Viewer session ${frozenDiagnostics.sessionGeneration} at ${frozenDiagnostics.snapshotAt}.`
      : `Exactly one fresh Viewer session did not start and reach running within this trial; performance counters are unavailable at ${frozenDiagnostics.snapshotAt}.`;
    trialStatus.textContent = `Run ended at ${endedAt}. ${diagnosticMessage} Choose completed or incomplete on export. The record remains in memory until downloaded or the page closes.`;
  });
  for (const button of root.querySelectorAll('[data-mark]')) {
    button.addEventListener('click', () => {
      const event = button.dataset.mark;
      if (event === 'stationary-window-marked') {
        try { windowRef.__browserArDiagnostics?.markStationary?.(); } catch { /* diagnostic controls never interrupt AR */ }
      }
      mark(event);
    });
  }
  root.querySelector('[data-action="reset-pose"]').addEventListener('click', () => {
    try { windowRef.__browserArDiagnostics?.resetPoseWindow?.(); } catch { /* diagnostic controls never interrupt AR */ }
    trialStatus.textContent = 'Relative-pose window reset and closed.';
  });

  function buildEvidence() {
    const diagnostics = frozenDiagnostics;
    const manual = {
      beginToFirstAlignedFrameMs: numberValue(form, 'beginToFirstAlignedFrameMs') ?? null,
      alignedOverlayTimingMethod: nullableText(fieldNode('alignedOverlayTimingMethod')?.value),
      registrationOffsetHorizontalMm: numberValue(form, 'registrationOffsetHorizontalMm') ?? null,
      registrationOffsetVerticalMm: numberValue(form, 'registrationOffsetVerticalMm') ?? null,
      registrationOffsetReference: textValue(form, 'registrationOffsetReference'),
      registrationOffsetMethod: textValue(form, 'registrationOffsetMethod'),
      videoStartupMs: numberValue(form, 'videoStartupMs') ?? null,
      concurrentVideosObserved: integerValue(form, 'concurrentVideosObserved') ?? null,
      falsePositiveCount: numberValue(form, 'falsePositiveCount') ?? null,
      rebufferCount: numberValue(form, 'rebufferCount') ?? null,
      tracking: textValue(form, 'tracking'),
      lag: textValue(form, 'lag'),
      glareAndOcclusion: textValue(form, 'glareAndOcclusion'),
      thermal: textValue(form, 'thermal'),
      memory: textValue(form, 'memory'),
      failures: textValue(form, 'failures'),
      tuningChanges: textValue(form, 'tuningChanges'),
      qualityRating: fieldNode('qualityRating').value,
    };
    const cameraResolutionPx = diagnostics.cameraResolutionPx;
    const cameraTrackResolutionPx = diagnostics.cameraTrackResolutionPx;
    const actualStart = startedAt;
    const actualEnd = endedAt;
    const trialState = fieldNode('trialState')?.value ?? 'incomplete';
    return {
      schemaVersion: 5,
      runId,
      seriesId: textValue(form, 'seriesId'),
      capturedAt: new Date().toISOString(),
      device: {
        family: fieldNode('deviceFamily').value,
        model: textValue(form, 'deviceModel'),
        variant: textValue(form, 'deviceVariant'),
        osVersion: textValue(form, 'osVersion'),
        buildNumber: textValue(form, 'buildNumber'),
      },
      browser: {
        family: fieldNode('browserFamily').value,
        name: textValue(form, 'browserName'),
        version: textValue(form, 'browserVersion'),
        embeddedHost: fieldNode('browserFamily').value === 'embedded-other' ? textValue(form, 'embeddedHost') : null,
      },
      build: {
        viewerBuildId,
        runtimeVersion: textValue(form, 'runtimeVersion'),
        engineVersion: textValue(form, 'engineVersion'),
      },
      fixture: {
        identifier: textValue(form, 'identifier'),
        contentRevision: textValue(form, 'contentRevision'),
        printLabel: textValue(form, 'printLabel'),
        expectedTargets: integerValue(form, 'expectedTargets'),
        loadedTargets: integerValue(form, 'loadedTargets'),
        expectedVideos: integerValue(form, 'expectedVideos'),
        loadedVideos: integerValue(form, 'loadedVideos'),
        concurrentVideosExpected: integerValue(form, 'concurrentVideosExpected'),
        presentationModes: [...fieldNode('presentationModes').selectedOptions].map(option => option.value),
      },
      conditions: {
        entryMethod: fieldNode('entryMethod').value,
        measurementPurpose: fieldNode('measurementPurpose').value,
        network: textValue(form, 'network'),
        lighting: textValue(form, 'lighting'),
        screencastingEnabled: fieldNode('screencastingEnabled').checked,
        renderCameraTexture: fieldNode('renderCameraTexture').checked,
        cameraSetting: textValue(form, 'cameraSetting'),
        cameraResolutionPx,
        cameraResolutionSource: diagnostics.cameraResolutionSource,
        cameraTrackResolutionPx,
        printDistanceCm: numberValue(form, 'printDistanceCm') ?? null,
        viewingAngleDegrees: numberValue(form, 'viewingAngleDegrees') ?? null,
        externalCameraUsed: fieldNode('externalCameraUsed').checked,
      },
      trial: {
        startedAt: actualStart,
        endedAt: actualEnd,
        state: trialState,
        phase: fieldNode('phase').value,
      },
      timeline,
      checklist: Object.fromEntries(qualificationChecklistKeys.map(key => [key, {
        state: form.querySelector(`[data-check-state="${key}"]`).value,
        note: textOrEmpty(form.querySelector(`[data-check-note="${key}"]`).value),
      }])),
      measurements: {diagnostics, manual},
      observations: textValue(form, 'observations') ?? '',
      privacy: {
        cameraFramesStored: false,
        signedMediaUrlsIncluded: false,
        sessionCredentialsIncluded: false,
        uploaded: false,
      },
    };
  }

  root.querySelector('[data-action="export"]').addEventListener('click', () => {
    validation.classList.remove('qar-warning');
    if (!startedAt || !endedAt) {
      validation.textContent = 'Begin and end the run before export. Incomplete trials may be exported after End run by selecting an incomplete state.';
      validation.classList.add('qar-warning');
      return;
    }
    if (!frozenDiagnostics) {
      validation.textContent = 'Diagnostics were not frozen when the run ended. Start and end a new run before export.';
      validation.classList.add('qar-warning');
      return;
    }
    const run = buildEvidence();
    const errors = validateQualificationRun(run);
    if (errors.length) {
      validation.textContent = `Evidence validation found ${errors.length} issue(s):\n${errors.map(error => `• ${error}`).join('\n')}`;
      validation.classList.add('qar-warning');
      return;
    }
    const blob = new Blob([`${JSON.stringify(run, null, 2)}\n`], {type: 'application/json'});
    const url = URL.createObjectURL(blob);
    const link = documentRef.createElement('a');
    link.href = url;
    link.download = `browser-ar-qualification-${run.device.family}-${run.browser.family}-${run.runId}.json`;
    link.style.display = 'none';
    documentRef.body.append(link);
    link.click();
    link.remove();
    windowRef.setTimeout(() => URL.revokeObjectURL(url), 1000);
    exported = true;
    validation.textContent = `Downloaded run ${run.runId}. Shape and privacy checks passed; this tool does not grade runtime quality or approve release.`;
  });

  return {root};
}

function textOrEmpty(value) {
  return typeof value === 'string' ? value.trim() : '';
}
